import type { IncomingMessage } from 'http';

import { EXPERIMENT_HEADER_PREFIX } from './experimentCookie';
import { isProductionEnv } from './isProductionEnv';
import { resolveExperiment } from './resolveExperiment';
import type {
  ExperimentAssignments,
  ExperimentDefinition,
  ResolveExperimentContext,
} from './types';

function parseCookieHeader(
  cookieHeader: string | undefined,
): Record<string, string> {
  if (!cookieHeader) return {};

  return cookieHeader.split(';').reduce<Record<string, string>>((acc, part) => {
    const [rawName, ...valueParts] = part.trim().split('=');
    if (!rawName) return acc;
    acc[rawName] = safeDecodeURIComponent(valueParts.join('='));
    return acc;
  }, {});
}

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function isVariantValid(
  experiment: ExperimentDefinition,
  variant: string,
): boolean {
  return variant in experiment.variants;
}

export function readExperimentAssignmentsFromHeaders(
  headers: IncomingMessage['headers'] | undefined,
): ExperimentAssignments {
  if (!headers) return {};

  const assignments: ExperimentAssignments = {};

  for (const [key, value] of Object.entries(headers)) {
    if (!key.startsWith(EXPERIMENT_HEADER_PREFIX)) continue;
    const name = key.slice(EXPERIMENT_HEADER_PREFIX.length);
    const variant = Array.isArray(value) ? value[0] : value;
    if (name && variant) {
      assignments[name] = variant;
    }
  }

  return assignments;
}

function readValidatedHeaderAssignment(
  experiment: ExperimentDefinition,
  headerAssignments: ExperimentAssignments,
): string | undefined {
  const variant = headerAssignments[experiment.name];
  if (!variant || !isVariantValid(experiment, variant)) {
    return undefined;
  }
  return variant;
}

export function resolveExperimentsFromReq(
  req: IncomingMessage | undefined,
  experiments: ExperimentDefinition[],
  options: {
    clientIp?: string;
    geoCountry?: string;
    language?: string;
    random?: () => number;
    urlSearch?: string;
  } = {},
): ExperimentAssignments {
  if (!req) return {};

  const headerAssignments = readExperimentAssignmentsFromHeaders(req.headers);
  const cookies = parseCookieHeader(req.headers.cookie);
  const urlSearch = options.urlSearch ?? '';
  const isProduction = isProductionEnv();
  const assignments: ExperimentAssignments = {};

  for (const experiment of experiments.filter((item) => item.shipped)) {
    const headerVariant = readValidatedHeaderAssignment(
      experiment,
      headerAssignments,
    );
    if (headerVariant) {
      assignments[experiment.name] = headerVariant;
      continue;
    }

    const context: ResolveExperimentContext = {
      clientIp: options.clientIp,
      geoCountry: options.geoCountry,
      language: options.language,
      isBot: false,
      isProduction,
      urlSearch,
      getCookie: (name) => cookies[name],
      random: options.random ?? Math.random,
    };

    assignments[experiment.name] = resolveExperiment(
      experiment,
      context,
    ).variant;
  }

  return assignments;
}
